import { cond, get, isEmpty, stubTrue } from 'lodash/fp';
import type {
  HandleGetNewCode,
  HandleRestorePassword,
  LoginConfig,
  SetFormType,
  SetSharedState,
} from 'src/components/AuthNew/types';
import { AuthFormType } from 'src/components/AuthNew/types';
import { api } from 'src/api';
import { flowAsync } from 'src/utils/fp';
import type {
  getClientExists,
  getClientExistsResponse,
  GetClientResendEmailConfirmationResponse,
  PostClientRestorePasswordResponse,
} from '@lt/api/dist/methods/client/types';
import type { CondPairUnary } from 'lodash';
import {
  isPasswordSet,
  isPasswordNotSet,
  openLoginWithPassword,
  openLoginWithSMSCode,
  isPasswordLeaked,
} from 'src/components/AuthNew/utils';
import { useRecaptchaController } from 'src/components/ReCaptchaModal';

type Props = {
  setFormType: SetFormType;
  handleGetNewCode: HandleGetNewCode;
  handleRestorePassword: HandleRestorePassword;
  setSharedState: SetSharedState;
};

const isClientExist = (response: getClientExistsResponse) =>
  response.success && !isEmpty(response?.client);

const ifClientExist = (
  phone: string | undefined,
  email: string | undefined,
  setFormType: SetFormType,
  handleGetNewCode: Props['handleGetNewCode'],
  handleRestorePassword: Props['handleRestorePassword'],
  setSharedState: SetSharedState,
): CondPairUnary<getClientExistsResponse, Promise<boolean> | undefined> => [
  isClientExist,
  getPhoneLoginCodeCond(
    phone,
    email,
    setFormType,
    handleGetNewCode,
    handleRestorePassword,
    setSharedState,
  ),
];

const isClientNotExist = (response: getClientExistsResponse): boolean =>
  get('success')(response) === false;

const openRegistrationForm = (setFormType: SetFormType) => (): undefined => {
  setFormType(AuthFormType.Registration);
  return undefined;
};

const ifClientNotExist =
  (setSharedState: SetSharedState) =>
  (setFormType: SetFormType) =>
  ({
    phone,
    type,
    email,
  }: LoginConfig): CondPairUnary<getClientExistsResponse, undefined> =>
    [
      isClientNotExist,
      () => {
        setSharedState((state) => ({
          ...state,
          email: email ?? state.email,
          phone: phone ?? state.phone,
          signUpType: type,
        }));
        openRegistrationForm(setFormType)();
        return undefined;
      },
    ];

const ifAuthByPhone = (phone: string | undefined) => () => Boolean(phone);
const ifAuthByEmail = stubTrue;

const ifPasswordSet =
  (setSharedState: SetSharedState) =>
  (setFormType: SetFormType) =>
  (email: string): CondPairUnary<getClientExistsResponse, void> =>
    [
      isPasswordSet,
      () => {
        setSharedState((prev) => ({ ...prev, email }));
        openLoginWithPassword(setFormType)();
      },
    ];

const ifPasswordNotSet =
  (handleRestorePassword: Props['handleRestorePassword']) =>
  (
    email: string,
  ): CondPairUnary<
    getClientExistsResponse,
    Promise<PostClientRestorePasswordResponse>
  > =>
    [isPasswordNotSet, () => handleRestorePassword(email, false)];

const ifPasswordLeaked =
  (handleRestorePassword: Props['handleRestorePassword']) =>
  (
    email: string,
  ): CondPairUnary<
    getClientExistsResponse,
    Promise<PostClientRestorePasswordResponse>
  > =>
    [isPasswordLeaked, () => handleRestorePassword(email, true)];

const getEmailLoginCond = (
  email: string,
  setFormType: SetFormType,
  handleRestorePassword: Props['handleRestorePassword'],
  setSharedState: SetSharedState,
) =>
  flowAsync(
    cond<
      getClientExistsResponse,
      void | Promise<
        | PostClientRestorePasswordResponse
        | GetClientResendEmailConfirmationResponse
        | boolean
        | CondPairUnary<
            getClientExistsResponse,
            Promise<GetClientResendEmailConfirmationResponse>
          >
      >
    >([
      ifPasswordSet(setSharedState)(setFormType)(email),
      ifPasswordLeaked(handleRestorePassword)(email),
      ifPasswordNotSet(handleRestorePassword)(email),
    ]),
  );

const getPhoneLoginCodeCond = (
  phone: string | undefined,
  email: string | undefined,
  setFormType: SetFormType,
  handleGetNewCode: Props['handleGetNewCode'],
  handleRestorePassword: Props['handleRestorePassword'],
  setSharedState: SetSharedState,
) =>
  cond<getClientExistsResponse, Promise<boolean> | undefined>([
    [
      ifAuthByPhone(phone),
      () => {
        setSharedState((prev) => ({ ...prev, phone: phone as string }));
        return flowAsync(
          handleGetNewCode,
          openLoginWithSMSCode(setFormType),
        )({
          phone,
          event: 'sign_in',
        });
      },
    ],
    [
      ifAuthByEmail,
      getEmailLoginCond(
        email as string,
        setFormType,
        handleRestorePassword,
        setSharedState,
      ),
    ],
  ]);

const getClientExistsRequest = (
  data: getClientExists,
): Promise<getClientExistsResponse> => api.getClientExists(data);

export const useLogin = ({
  handleGetNewCode,
  handleRestorePassword,
  setFormType,
  setSharedState,
}: Props) => {
  const { isOpenReCaptcha, captchaCallback, handleRecaptchaRequired } =
    useRecaptchaController();
  async function handleLogin({
    email,
    phone,
    type,
  }: LoginConfig): Promise<void> {
    const clientExistRequest = {
      email: type === 'email' ? email : undefined,
      phone: type === 'phone' ? phone : undefined,
    };

    try {
      const response = await getClientExistsRequest(clientExistRequest);

      if (!response.success && response.code === 'captcha_required') {
        handleRecaptchaRequired(() => handleLogin({ email, phone, type }));
        return;
      }

      await cond<getClientExistsResponse, Promise<boolean> | undefined>([
        ifClientExist(
          phone,
          email,
          setFormType,
          handleGetNewCode,
          handleRestorePassword,
          setSharedState,
        ),
        ifClientNotExist(setSharedState)(setFormType)({ email, phone, type }),
      ])(response);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setFormType(AuthFormType.Failure);
    }
  }

  return { handleLogin, isOpenReCaptcha, captchaCallback };
};
